import { useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { MAX_CAROUSEL_ITEM_COUNT } from './common/constants'
import { LOOKING_FOR_SOMETHING_ELSE_CONTENT } from '../LookingForSomethingElse/constants'
import { trendingCruiseDestinations } from '../TrendingDestinations/constants'
import { CRUISE_BANK_UNIFIED_FILTERS } from 'constants/cruise'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import Places from 'constants/places'
import GeoContext from 'contexts/geoContext'
import { useLocalizedPlaces } from 'hooks/usePlace'
import useCruiseInternationalMarket from 'hooks/useCruiseInternationalMarket'

const { sortBy: cruiseBankSortBy, ...CRUISE_BANK_FILTERS } = CRUISE_BANK_UNIFIED_FILTERS

export const DEFAULT_CRUISE_FILTERS: App.OfferListFilters = {
  ...CRUISE_BANK_FILTERS,
  frontPageVisibleOnly: true,
  limit: MAX_CAROUSEL_ITEM_COUNT,
}

const MANUALLY_SORTED_CRUISE_FILTERS: App.OfferListFilters = {
  ...DEFAULT_CRUISE_FILTERS,
  sortBy: cruiseBankSortBy,
}

interface Props {
  title?: React.ReactNode
  minimumOffersCount?: number
}

function CruiseOfferCarousel(props: Props) {
  const {
    title,
    minimumOffersCount,
  } = props
  const intl = useIntl()
  const isInternationalMarket = useCruiseInternationalMarket()
  const filters = isInternationalMarket || !isManualSortingEnabledFor('cruises')
    ? DEFAULT_CRUISE_FILTERS
    : MANUALLY_SORTED_CRUISE_FILTERS
  const { currentRegionCode } = useContext(GeoContext)

  const anywhereLabel = intl.formatMessage({
    id: 'xgsbMHpJ0hZoqyyDo8fb',
    defaultMessage: 'Around the world',
  })

  const regionalDestinations = useMemo(
    () => trendingCruiseDestinations[currentRegionCode] ?? [],
    [currentRegionCode],
  )

  const placeIds = useMemo(
    () => regionalDestinations.map(destination => destination.placeId),
    [regionalDestinations],
  )
  const localizedPlaces = useLocalizedPlaces(placeIds)

  const destinations = useMemo(() => {
    const resolvedDestinations = regionalDestinations.flatMap((destination) => {
      const name = localizedPlaces[destination.placeId]?.place?.name
      if (!name) {
        return []
      }

      return [{
        value: destination.placeId,
        label: name,
      }]
    })

    return [{ value: Places.Anywhere.id, label: anywhereLabel }, ...resolvedDestinations]
  }, [anywhereLabel, regionalDestinations, localizedPlaces])

  const to = useMemo(() => {
    return LOOKING_FOR_SOMETHING_ELSE_CONTENT.cruises.to
  }, [])

  const defaultTitle = <FormattedMessage
    id="ATLd-dDHI3xjQEm30tD3"
    defaultMessage="Set sail on the ultimate <i>cruise</i>"
    
    values={{
      i: (chunks) => <i>{chunks}</i>,
    }}
  />

  return (
    <OfferCardCarouselSection
      filters={filters}
      title={title ?? defaultTitle}
      allLink="/cruises"
      additionalFilters="destinations"
      additionalFilterOptions={destinations}
      defaultFilter={Places.Anywhere.id}
      tileStyle="card-default"
      tileSize="medium"
      minimumOffersCount={minimumOffersCount}
      data-testid="cruise-carousel"
    >
      <OfferCarouselLookingForSomethingElse to={to} />
    </OfferCardCarouselSection>
  )
}

export default React.memo(CruiseOfferCarousel)
